// Reverse geocoding utilities using Mapbox API

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * Reverse geocode coordinates to get a human-readable address
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {Promise<string>} Address string or coordinates if geocoding fails
 */
export const reverseGeocode = async (latitude, longitude) => {
  // Return coordinates if no valid token
  if (!MAPBOX_TOKEN || MAPBOX_TOKEN.includes('demo')) {
    return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
  }

  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}&types=address,poi`
    );

    if (!response.ok) {
      throw new Error(`Geocoding API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.features && data.features.length > 0) {
      const feature = data.features[0];
      
      // Use the full place_name which includes complete address details
      if (feature.place_name) {
        return feature.place_name;
      }
      
      // Fallback: build address from components
      const addressComponents = feature.context || [];
      const addressParts = [];
      
      // Add house number and street
      if (feature.properties?.address) {
        addressParts.push(feature.properties.address);
      }
      if (feature.text && !addressParts.includes(feature.text)) {
        addressParts.push(feature.text);
      }
      
      // Add neighborhood/area
      const neighborhood = addressComponents.find(c => c.id?.includes('neighborhood'))?.text;
      if (neighborhood) {
        addressParts.push(neighborhood);
      }
      
      // Add city
      const city = addressComponents.find(c => c.id?.includes('place'))?.text;
      if (city) {
        addressParts.push(city);
      }
      
      // Add region (state/province)
      const region = addressComponents.find(c => c.id?.includes('region'))?.text;
      if (region) {
        addressParts.push(region);
      }
      
      // Add country if not Canada/US (for international)
      const country = addressComponents.find(c => c.id?.includes('country'))?.text;
      if (country && !['Canada', 'United States'].includes(country)) {
        addressParts.push(country);
      }
      
      return addressParts.length > 0 ? addressParts.join(', ') : feature.place_name || 'Address not found';
    }

    // Fallback to coordinates if no address found
    return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
    
  } catch (error) {
    console.warn('Reverse geocoding failed:', error);
    return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
  }
};

/**
 * Get a short address (street and number, plus city) for compact display
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {Promise<string>} Short address or coordinates
 */
export const reverseGeocodeShort = async (latitude, longitude) => {
  const fullAddress = await reverseGeocode(latitude, longitude);
  
  // If it's coordinates, return as-is
  if (fullAddress.includes(',') && fullAddress.match(/^\d+\.\d+,\s*-?\d+\.\d+$/)) {
    return fullAddress;
  }

  // For short display, show street + city (first and second-to-last parts)
  const parts = fullAddress.split(',').map(p => p.trim());
  
  if (parts.length >= 3) {
    // Show: "123 Main Street, Toronto" (street + city)
    const street = parts[0];
    const city = parts[parts.length - 2]; // Second to last is usually city
    return `${street}, ${city}`;
  } else if (parts.length === 2) {
    // Show both parts if only 2
    return fullAddress;
  }
  
  // Fallback to just the first part
  return parts[0] || fullAddress;
};

/**
 * Cache for reverse geocoded addresses to avoid repeated API calls
 */
class AddressCache {
  constructor() {
    this.cache = new Map();
    this.maxSize = 100;
  }

  getKey(lat, lng) {
    // Round to 4 decimal places for cache key (about 11m precision)
    return `${lat.toFixed(4)},${lng.toFixed(4)}`;
  }

  get(lat, lng) {
    return this.cache.get(this.getKey(lat, lng));
  }

  set(lat, lng, address) {
    const key = this.getKey(lat, lng);
    
    // Remove oldest entries if cache is full
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    
    this.cache.set(key, address);
  }

  has(lat, lng) {
    return this.cache.has(this.getKey(lat, lng));
  }
}

const addressCache = new AddressCache();

/**
 * Cached reverse geocoding - checks cache first
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {Promise<string>} Cached or fresh address
 */
export const reverseGeocodeCached = async (latitude, longitude) => {
  // Check cache first
  if (addressCache.has(latitude, longitude)) {
    return addressCache.get(latitude, longitude);
  }

  // Get fresh address and cache it
  const address = await reverseGeocode(latitude, longitude);
  addressCache.set(latitude, longitude, address);
  
  return address;
};

/**
 * Cached short reverse geocoding
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {Promise<string>} Cached or fresh short address
 */
export const reverseGeocodeShortCached = async (latitude, longitude) => {
  const cacheKey = `short_${latitude.toFixed(4)},${longitude.toFixed(4)}`;
  
  // Simple cache check for short addresses
  if (addressCache.cache.has(cacheKey)) {
    return addressCache.cache.get(cacheKey);
  }

  const shortAddress = await reverseGeocodeShort(latitude, longitude);
  addressCache.cache.set(cacheKey, shortAddress);
  
  return shortAddress;
};
